// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, {useEffect, useState} from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router";

import  queryString from "query-string";

// semantic-ui
import "semantic-ui-css/semantic.css";

// pages
import Home from "pages/Home";
// import GettingStarted from 'pages/GettingStarted'

import Dashboard from "pages/Dashboard";
import Apis from "pages/Apis";

import { Admin } from "pages/Admin";
import WebSocketAPIs from "pages/WebSocketAPIs";
import EventBasedApis from "pages/EventBasedAPIs";
import EndpointSecurity from "./pages/EndpointSecurity";

// components
import AlertPopup from "components/AlertPopup";
import GlobalModal from "components/Modal";
import NavBar from "components/NavBar";
import Feedback from "./components/Feedback";

import {
  isAdmin,
  isAuthenticated,
  isRegistered,
  isRestAPIUser,
  isWsAPIUser,
  isIkanoUser,
  init,
  login,
  logout,
  isDeveloperToolsUser,
} from "services/self";
import "./index.css";
import Welcome from "components/Welcome";
import DeveloperTools from "pages/DeveloperTools/DeveloperTools";
import Documentation from "pages/Documentation/Documentation";

import { createRoot } from 'react-dom/client';

// TODO: Feedback should be enabled if
// the following is true && the current
// user is not an administrator
const feedbackEnabled = window.config.feedbackEnabled;

export const RegisteredRoute = ({ children }) => {
  return isRegistered() ? children : <Navigate to="/" />;
};

export const AdminRoute = ({ children }) => {
  return isAdmin() ? children : <Navigate to="/" />;
};

export const RestRoute = ({ children }) => {
  return isRestAPIUser() ? children : <Navigate to="/" />;
};

export const WsRoute = ({ children }) => {
  return isWsAPIUser() ? children : <Navigate to="/" />;
};

export const EventBasedRoute = ({ children }) => {
  return isIkanoUser() ? children : <Navigate to="/" />;
};

export const EndpointSecurityRoute = ({ children }) => {
  return isRestAPIUser() || isWsAPIUser() ? children : <Navigate to="/" />;
};

export const DeveloperToolsRoute = ({ children }) => {
  return isDeveloperToolsUser() ? children : <Navigate to="/" />;
};

export const IkanoUserRoute = ({ children }) => {
  return isIkanoUser() ? children : <Navigate to="/" />;
};

// To shut up a dev warning
const HomeWrap = (props) => <Home {...props} />;
const DocumentationWrap = (props) => <Documentation {...props} />;
const WebsocketApisWrap = (props) => <WebSocketAPIs {...props} />;
const EventBasedApisWrap = (props) => <EventBasedApis {...props} />;
const DashboardWrap = (props) => <Dashboard {...props} />;
const EndpointSecurityWrap = (props) => <EndpointSecurity {...props} />;
const DeveloperToolsWrap = (props) => <DeveloperTools {...props} />;

const LoginWrapper = () => {
  useEffect(() => {
    login();
  }, []);
  return <Navigate to="/" />;
};
const LogoutWrapper = () => {
  useEffect(() => {
    logout();
  }, []);
  return <Navigate to="/" />;
};
const indexWrapper = () => {
  const { action } = queryString.parse(window.location.search);
  if (action === "login") {
    login();
  } else if (action === "logout") {
    logout();
  }
  return <Navigate to="/" />;
};
const HomePageWrapper = () => {
  const [auth, setAuth]  = useState(isAuthenticated());
  useEffect(() => {
    setAuth(isAuthenticated());
  }, []);
  return auth ? <Welcome /> : <HomeWrap />;
};

class App extends React.Component {
  constructor() {
    super();
    init();

    // We are using an S3 redirect rule to prefix the url path with #!
    // This then converts it back to a URL path for React routing
    if (window.location.hash && window.location.hash[1] === "!") {
      const hashRoute = window.location.hash.substring(2);
      window.history.pushState({}, "home page", hashRoute);
    }
  }

  render() {
    return (
      <BrowserRouter>
        <NavBar />
        <GlobalModal />
        <Routes>
          <Route 
            path="/"
            element={<HomePageWrapper/>}
          />
          <Route
            path="/index.html"
            element={indexWrapper()}
          />

          <Route path="/websocket-apis" element={<WsRoute><WebsocketApisWrap /></WsRoute>} />
          <Route
            path="/websocket-apis/*"
            element={<WsRoute><WebsocketApisWrap /></WsRoute>}
          />
          <Route
            path="/event-based-apis"
            element={<EventBasedRoute><EventBasedApisWrap /></EventBasedRoute>}
          />
          <Route
            path="/event-based-apis/*"
            element={<EventBasedRoute><EventBasedApisWrap /></EventBasedRoute>}
          />
          <Route
            path="/endpoint-security/*"
            element={<EndpointSecurityRoute><EndpointSecurityWrap /></EndpointSecurityRoute>}
          />
          <Route path="/dashboard" element={<DashboardWrap />} />
          <Route
            path="developer-tools/*"
            element={<RegisteredRoute><DeveloperToolsWrap /></RegisteredRoute>}
          />
          <Route
            path="/documentation/*"
            element={<DocumentationWrap />}
          />
          <Route path="/admin/*" element={<Admin />} />
          <Route path="/apis" element={<RestRoute><Apis /></RestRoute>} />
          <Route path="/apis/:apiName" element={<RestRoute><Apis /></RestRoute>} />
          <Route path="/apis/:apiName/:stage" element={<RestRoute><Apis /></RestRoute>} />
          <Route
            path="/login"
            element={<LoginWrapper />}
          />
          <Route
            path="/logout"
            element={<LogoutWrapper />}
          />
          <Route
            element={() =>
              isAuthenticated() ? <h2>Page not found</h2> : <Navigate to="/" />
            }
          />
        </Routes>
        {feedbackEnabled && <Feedback />}
        <AlertPopup />
      </BrowserRouter>
    );
  }
}
const container = document.getElementById('root');
const root = createRoot(container);
root.render(<App />);
